import { icon, number } from "../../../config";
import { splitString } from "../../../utils";

/**
 * Sorts grid data based on the provided sort ID and order
 * @author Sarthak Arora
 */
export const sortGridData = (data, sortById, order) => {
  if (!data || !Array.isArray(data)) return data;

  const sortedData = [...data];
  const isAsc = order === "asc";

  switch (sortById) {
    case number.ONE:
      sortedData.sort((a, b) => (a.QueueName?.trim() || "").localeCompare(b.QueueName?.trim() || ""));
      break;

    case number.TWO:
      sortedData.sort((a, b) => {
        const totalTasksA = a.details?.reduce((sum, team) => sum + (team.taskCount || 0), 0) || 0;
        const totalTasksB = b.details?.reduce((sum, team) => sum + (team.taskCount || 0), 0) || 0;
        return totalTasksA - totalTasksB;
      });
      break;

    case number.THREE:
      sortedData.sort((a, b) => (a.projectCount || 0) - (b.projectCount || 0));
      break;

    default:
      break;
  }

  if (!isAsc) {
    sortedData.reverse();
  }
  return sortedData;
};

/**
 * Sort options for queue list
 * @author Bhavana
 */
export const sortOptions = [
  { id: number.ONE, label: "Alphabetical" },
  { id: number.TWO, label: "Open Tasks" },
  { id: number.THREE, label: "Teams" },
];

/**
 * Sort order options
 * @author Bhavana
 */
export const sortOrders = [
  { id: "asc", label: "Ascending" },
  { id: "desc", label: "Descending" },
];

/**
 * Returns the appropriate sort icon based on column key and sort configuration
 * @author Bhavana
 */
export const getSortIcon = (columnKey, cardIndex, sortConfigs) => {
  const sortConfig = sortConfigs[cardIndex];
  if (!sortConfig || sortConfig.key !== columnKey) {
    return;
  }
  return sortConfig.direction === "asc" ? <span className='queue-sort-icon'>{icon?.ASCENDING}</span> : <span className='queue-sort-icon'>{icon?.DESCENDING}</span>;
};

/**
 * Sorts and filters data based on search term and sort configuration
 * @author Bhavana
 */
export const getSortedData = (data, cardIndex, sortConfigs, searchTerm = "") => {
  let filteredData = data?.filter((item) => item?.projectStatus !== number.ONE);

  if (searchTerm && searchTerm.trim() !== "") {
    filteredData = data.filter((item) => item.ProjectName && item.ProjectName.toLowerCase().includes(searchTerm.toLowerCase()) && item?.projectStatus !== number.ONE);
  }

  const sortConfig = sortConfigs[cardIndex];
  if (!sortConfig || !sortConfig.key) return filteredData;

  return [...filteredData].sort((a, b) => {
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];

    if (sortConfig.key === "members" || sortConfig.key === "taskCount") {
      aValue = parseInt(aValue) || number.ZERO;
      bValue = parseInt(bValue) || number.ZERO;
    }

    if (typeof aValue === "string") {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) {
      return sortConfig.direction === "asc" ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortConfig.direction === "asc" ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Filters data based on search term
 * @author Bhavana
 * @param {Array} data - Array of data to filter
 * @param {string} searchTerm - Search term to filter by
 * @returns {Array} Filtered array of data
 */
export const filterDataBySearch = (data, searchTerm) => {
  if (!searchTerm || searchTerm.trim() === "") return data;

  return data.filter((item) => item.ProjectName && item.ProjectName.toLowerCase().includes(searchTerm.toLowerCase()));
};

/**
 * Builds updated queue object after owners change (member count, name, role).
 * @param {Object} originalQueue - Current queue data
 * @param {Array} newOwners - New owner objects (e.g. [{ value, label, Email }])
 * @param {Object} payload - Update payload with owners array (owner ids)
 * @param {string} [userId] - Current user id to determine if they remain owner
 * @returns {Object} Updated queue object
 */
export const getQueueWithUpdatedOwners = (originalQueue, newOwners, payload, userId) => {
  const ownerIds = newOwners.map((owner) => owner?.value?.toString());
  const previousOwnerCount = originalQueue?.owners ? splitString(originalQueue.owners, ",")?.length || 0 : 0;
  const ownerCountDelta = newOwners.length - previousOwnerCount;
  const currentMemberCount = originalQueue?.memberCount ?? 0;
  const isCurrentUserStillOwner = ownerIds.includes(userId?.toString());
  return {
    ...originalQueue,
    owners: payload?.owners,
    memberCount: Math.max(0, currentMemberCount + ownerCountDelta),
    Name: newOwners[0]?.label ?? newOwners[0]?.Email ?? originalQueue?.Name,
    RoleId: isCurrentUserStillOwner ? originalQueue?.RoleId : number.ONE,
  };
};

/**
 * Handles sorting logic and updates sort configurations
 * @author Bhavana
 */
export const handleSort = (key, cardIndex, sortConfigs, setSortConfigs) => {
  const currentConfig = sortConfigs[cardIndex] || { key: null, direction: "asc" };
  let direction = "asc";
  if (currentConfig.key === key && currentConfig.direction === "asc") {
    direction = "desc";
  }
  setSortConfigs((prev) => ({
    ...prev,
    [cardIndex]: { key, direction },
  }));
};
